import * as React from "react";
import { routes, Status } from "../../../../common/constants";
import {
  fetchOrganizationData,
  selector as organizationsApi,
} from "../../listOrganizationSlice";
import OrganizationsColumns from "./organizationsColumns";
import { useEffect, useMemo, useState } from "react";
import { useSpinner } from "../../../../customHooks";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  sendEmail,
  selector as sendEmailApi,
} from "../organizationUsers/sendEmailSlice";
import {
  IOrganization,
  IOrganizations,
} from "../../../../common/types/groupTypes";
import {
  selector as updateFirmwareVersion,
  setShow as setShowFirmware,
  setOrgId as setOrgIdFirmware,
} from "../../../../components/modals/updateFIrmwareVersion/updateFirmwareSlice";
import {
  selector as updateIotAgentVersion,
  setShow as setShowIotAgent,
  setOrgId as setOrgIdIotAgent,
  IUpdateResponse,
} from "../../../../components/modals/udpateIotAgentVersion/updateIotAgentSlice";
import {
  selector as listFirmwareApi,
  fetchFirmwareData,
} from "../../../manageUpdates/firmwareSlice";
import {
  selector as listIotAgentApi,
  fetchIotData,
} from "../../../manageUpdates/iotAgentSlice";
import { IListFirmwares } from "../../../../common/types/firmwareTypes";
import { IListIotAgent } from "../../../../common/types/iotAgentTypes";
import { isSuccessResponse } from "../../../../common/utils";
import useNotification from "../../../../components/notification/useNotification";
import { selector as editOrganizationsApi } from "../../../../components/modals/editOrgAdmin/editOrgSlice";
import { IInitialSliceState } from "../../../../common/types/commonTypes";

const useOrganizations = () => {
  const organizationsData: IOrganizations = useSelector(organizationsApi);
  const editOrganizationsData: IInitialSliceState =
    useSelector(editOrganizationsApi);
  const listFirmwaresResponse: IListFirmwares = useSelector(listFirmwareApi);
  const listIotAgentsResponse: IListIotAgent = useSelector(listIotAgentApi);
  const updateFirmwareVersionResponse = useSelector(updateFirmwareVersion);
  const updateIotAgentVersionResponse = useSelector(updateIotAgentVersion);
  const sendEmailApiResponse = useSelector(sendEmailApi);
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const showFirmware: boolean = useSelector(updateFirmwareVersion)?.show;
  const showIotAgent: boolean = useSelector(updateIotAgentVersion)?.show;
  const [globalFilter, setGlobalFilter] = React.useState<string>("");
  const [organizationsTableData, setOrganizationsTableData] = useState<
    IOrganization[]
  >([]);
  const [showAssignTemplates, setShowAssignTemplates] =
    React.useState<boolean>(false);
  const [assignOrgId, setAssignOrgId] = React.useState<string | null>(null);
  const { showSuccessNotification, showWarningNotification } =
    useNotification();
  const status: string =
    organizationsData.status === Status.PENDING
      ? organizationsData.status
      : listFirmwaresResponse.status === Status.PENDING
        ? listFirmwaresResponse.status
        : listIotAgentsResponse.status === Status.PENDING
          ? listIotAgentsResponse.status
          : updateIotAgentVersionResponse.status === Status.PENDING
            ? updateIotAgentVersionResponse.status
            : updateFirmwareVersionResponse.status === Status.PENDING
              ? updateFirmwareVersionResponse.status
              : editOrganizationsData.status === Status.PENDING
                ? editOrganizationsData.status
                : sendEmailApiResponse.status === Status.PENDING
                  ? sendEmailApiResponse.status
                  : "";

  useSpinner(status);

  useEffect(() => {
    const isOrgDataUpdated: boolean =
      organizationsData?.organizations &&
      organizationsData.organizations.value !== organizationsTableData;
    if (isOrgDataUpdated) {
      setOrganizationsTableData(organizationsData.organizations.value);
    }
  }, [organizationsData?.organizations]);

  if (organizationsData.status == Status.IDLE) {
    dispatch(fetchOrganizationData());
  }

  useEffect(() => {
    if (listFirmwaresResponse.status === Status.IDLE) {
      dispatch(fetchFirmwareData());
    }
    if (listIotAgentsResponse.status === Status.IDLE) {
      dispatch(fetchIotData());
    }
  }, [dispatch, listFirmwaresResponse.status, listIotAgentsResponse.status]);

  const handleFirmwareRowSelection = React.useCallback((data) => {
    dispatch(setOrgIdFirmware(data));
    dispatch(setShowFirmware(!showFirmware));
  }, []);

  const handleViewRowSelection = async (displayName: string, orgId: string) => {
    navigate(
      routes.organizationUsers + "?orgName=" + displayName + "&orgId=" + orgId,
    );
  };

  const handleIotAgentRowSelection = React.useCallback((data) => {
    dispatch(setOrgIdIotAgent(data));
    dispatch(setShowIotAgent(!showIotAgent));
  }, []);

  const handleAssignTemplates = React.useCallback((orgId: string) => {
    setAssignOrgId(orgId);
    setShowAssignTemplates(true);
  }, []);

  const handleReinvite = async (name: string, email: string) => {
    try {
      await dispatch(sendEmail({ name, email })).then((response) => {
        if (isSuccessResponse(response.payload)) {
          showSuccessNotification(response.payload.message);
        } else {
          showWarningNotification(response.error.message);
        }
      });
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  const organizationsColumns = useMemo(
    () =>
      OrganizationsColumns(
        navigate,
        handleFirmwareRowSelection,
        handleIotAgentRowSelection,
        handleReinvite,
        handleViewRowSelection,
        handleAssignTemplates,
      ),
    [],
  );

  return {
    organizationsTableData,
    organizationsColumns,
    globalFilter,
    setGlobalFilter,
    showFirmware,
    showIotAgent,
    showAssignTemplates,
    setShowAssignTemplates,
    assignOrgId,
  };
};

export default useOrganizations;
